// "Since you parked this" (2026-10-27, docs/decisions/2026-10-27-park-saves-
// work.md, docs/contact-view.md "Parking"): shown at the top of the
// suggestion when unpark's regeneration changed the steps or the reply. It
// lists what happened since the park (one plain line per fact, old → new),
// then each change in its own card (2026-10-28): "Change 1 of 2 · Steps" with
// the steps added, removed or with different defaults (each with its own
// pair when there are several), "Change 2 of 2 · Reply" with the CSR's reply
// beside the new draft, the differences highlighted. Each card has "Keep my
// saved work" / "Use new suggestion"; once picked it shows the choice with
// Undo. Under a divider, "Apply to all N changes:" with "Keep all my saved
// work" / "Use all new suggestions" (hidden with one change: the card's
// buttons do the same); they pick for every card. Once every change has a
// pick, that row goes (an Undo brings it back) and "Done" applies them and
// closes the panel; until then every pick can be undone. Only facts or a new message changed: no panel (the info strip,
// ParkInfoStrip.jsx, says what changed). Choosing only changes the panel:
// nothing is processed or sent until Process / Send. It takes the place of
// the restored / checked strip, in the same style with a warning accent, and
// carries the "Restored your work from …" line under its title (2026-10-27).

import { Box, Button, Divider, Link, Paper, Stack, SvgIcon, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  PARK_KEEP_LABEL, PARK_USE_NEW_LABEL, PARK_KEEP, PARK_NEW,
  sinceParkedTitle, etDateTime, wordDiff,
} from '@cxhub/shared'
import {
  compareItems, REPLY_ITEM, stepItemId, compareChanges, changeChoice, changeHeading, changesCount,
  changeDecided, allChangesDecided, applyToAllLabel, isEmptyReply, PARK_CHANGE_STEPS, PARK_KEEP_ALL_LABEL,
  PARK_USE_NEW_ALL_LABEL, PARK_KEPT_TEXT, PARK_USING_NEW_TEXT, PARK_NO_NEW_REPLY_TEXT, PARK_DONE_LABEL,
  PARK_PICKED_PER_STEP_TEXT, showApplyToAll,
} from './workDraft.js'

export { compareItems, REPLY_ITEM, stepItemId }

function CheckIcon({ sx }) {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round', fontSize: '1.1rem', ...sx }}>
      <polyline points="20 6 9 17 4 12" />
    </SvgIcon>
  )
}

function ChoiceButtons({ onChoose, disabled, testId }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }} data-testid={testId}>
      <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChoose(PARK_KEEP)}>{PARK_KEEP_LABEL}</Button>
      <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChoose(PARK_NEW)}>{PARK_USE_NEW_LABEL}</Button>
    </Stack>
  )
}

// value: PARK_KEEP / PARK_NEW, or null for a card whose steps were picked
// differently ("Picked per step").
function ChosenLine({ value, onUndo, disabled, testId }) {
  const text = value === PARK_KEEP ? PARK_KEPT_TEXT : value === PARK_NEW ? PARK_USING_NEW_TEXT : PARK_PICKED_PER_STEP_TEXT
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }} data-testid={testId}>
      <CheckIcon sx={{ color: 'success.main' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{text}</Typography>
      <Link component="button" type="button" variant="body2" disabled={disabled} onClick={onUndo} data-testid={`${testId}-undo`}>Undo</Link>
    </Stack>
  )
}

function DiffText({ parts, side }) {
  return (
    <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
      {parts.map((p, i) => {
        if (p.type === 'same') return <span key={i}>{p.text}</span>
        if (side === 'saved' && p.type === 'removed') return <Box component="mark" key={i} sx={{ bgcolor: 'warning.light', color: 'inherit' }}>{p.text}</Box>
        if (side === 'fresh' && p.type === 'added') return <Box component="mark" key={i} sx={{ bgcolor: 'success.light', color: 'inherit' }}>{p.text}</Box>
        return null
      })}
    </Typography>
  )
}

// Each step on its own line; with several, each has its own pair (or its
// pick with Undo), so one can be kept and another replaced (2026-10-28).
function StepsBody({ steps, perStep, choices, onChoose, busy }) {
  const line = (kind, s, text) => {
    const id = stepItemId(kind, s.index)
    const testId = `park-compare-step-${kind}-${s.index}`
    const value = choices?.[id] ?? null
    return (
      <Box key={`${kind}-${s.index}`} data-testid={testId}
        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', py: 0.5, ...(perStep ? { borderTop: 1, borderColor: 'divider' } : {}) }}>
        <Box sx={{ minWidth: 0, opacity: perStep && value ? 0.6 : 1 }}>
          <Typography variant="body2">{text}</Typography>
          {(s.changes ?? []).map((c) => <Typography key={c.field} variant="caption" component="div" color="text.secondary">{c.text}</Typography>)}
        </Box>
        {perStep && (value
          ? <ChosenLine value={value} disabled={busy} onUndo={() => onChoose?.([id], null)} testId={`${testId}-chosen`} />
          : <ChoiceButtons disabled={busy} onChoose={(v) => onChoose?.([id], v)} testId={`${testId}-choice`} />)}
      </Box>
    )
  }
  return (
    <Box data-testid="park-compare-steps">
      {(steps.changed ?? []).map((s) => line('changed', s, `Different: ${s.name}`))}
      {(steps.added ?? []).map((s) => line('added', s, `Added: ${s.name}`))}
      {(steps.removed ?? []).map((s) => line('removed', s, `Removed: ${s.name}`))}
    </Box>
  )
}

function ReplyBody({ reply }) {
  const noNew = isEmptyReply(reply.fresh)
  // With no new reply there is nothing to compare against: the CSR's reply
  // shows as it is, never highlighted as all removed.
  const parts = noNew ? [{ type: 'same', text: reply.saved }] : wordDiff(reply.saved, reply.fresh)
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }} data-testid="park-compare-reply">
      <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="park-compare-reply-saved">
        <Typography variant="caption" color="text.secondary">Your reply</Typography>
        <DiffText parts={parts} side="saved" />
      </Box>
      <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="park-compare-reply-new">
        <Typography variant="caption" color="text.secondary">New draft</Typography>
        {noNew
          ? <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }} data-testid="park-compare-reply-none">{PARK_NO_NEW_REPLY_TEXT}</Typography>
          : <DiffText parts={parts} side="fresh" />}
      </Box>
    </Box>
  )
}

// onChoose(itemIds, value): value 'kept' / 'new' for those items (a card's
// or one step's), or null for their Undo. onKeepAll / onUseNewAll pick for
// every item; onDone applies the picks. title / processedText: the panel's
// own wording when it compares for another reason than a park (a contact
// regenerated under a stale tab, 2026-10-28).
export default function ParkComparePanel({ restoredLine = null, compare, choices = {}, onChoose, onKeepAll, onUseNewAll, onDone, busy = false, error = null, title = null, processedText = 'Already processed before you parked this, not offered again' }) {
  if (!compare) return null
  const changes = compareChanges(compare)
  const total = changes.length
  const done = allChangesDecided(compare, choices)
  // The "Apply to all" row goes once everything is picked (Done is left).
  const allRow = showApplyToAll(compare, choices)
  const steps = compare.steps ?? {}
  return (
    <Paper variant="outlined" data-testid="park-compare-panel"
      sx={{ p: 1.5, mb: 1.5, borderColor: 'warning.main', borderLeftWidth: 4, bgcolor: (t) => alpha(t.palette.warning.main, t.palette.mode === 'dark' ? 0.12 : 0.07) }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
        {title ?? sinceParkedTitle(compare.since)}
        {total > 0 && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }} data-testid="park-compare-count"> · {changesCount(total)}</Box>}
      </Typography>
      {restoredLine && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }} data-testid="park-compare-restored">{restoredLine}</Typography>
      )}
      {(compare.messages?.length > 0 || compare.facts?.length > 0) && (
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1 }} data-testid="park-compare-facts">
          {(compare.messages ?? []).map((m) => (
            <li key={`m-${m.id ?? m.at}`}>
              <Typography variant="body2">New customer message, {etDateTime(m.at)}: <Box component="span" sx={{ fontStyle: 'italic' }}>{m.text}</Box></Typography>
            </li>
          ))}
          {(compare.facts ?? []).map((f, i) => <li key={`f-${i}`}><Typography variant="body2">{f.text}</Typography></li>)}
        </Box>
      )}
      {(steps.processed ?? []).length > 0 && (
        <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 1 }} data-testid="park-compare-processed">
          {processedText}: {steps.processed.map((s) => s.name).join(', ')}
        </Typography>
      )}
      {total > 0 && (
        <Stack spacing={1.5} sx={{ mb: 1 }}>
          {changes.map((c, i) => {
            const value = changeChoice(c, choices)
            const decided = changeDecided(c, choices)
            const perStep = c.key === PARK_CHANGE_STEPS && c.items.length > 1
            const testId = `park-compare-change-${c.key}`
            const choiceTestId = c.key === PARK_CHANGE_STEPS ? 'park-compare-steps-choice' : 'park-compare-reply-choice'
            return (
              <Paper key={c.key} variant="outlined" data-testid={testId} data-chosen={decided ? (value ?? 'mixed') : ''} sx={{ p: 1.25, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid={`${testId}-heading`}>{changeHeading(c, i + 1, total)}</Typography>
                  {decided
                    ? <ChosenLine value={value} disabled={busy} onUndo={() => onChoose?.(c.items, null)} testId={`${choiceTestId}-chosen`} />
                    : <ChoiceButtons disabled={busy} onChoose={(v) => onChoose?.(c.items, v)} testId={choiceTestId} />}
                </Box>
                <Box sx={{ opacity: decided && !perStep ? 0.6 : 1, transition: 'opacity 120ms' }}>
                  {c.key === PARK_CHANGE_STEPS
                    ? <StepsBody steps={steps} perStep={perStep} choices={choices} onChoose={onChoose} busy={busy} />
                    : <ReplyBody reply={compare.reply} />}
                </Box>
              </Paper>
            )
          })}
        </Stack>
      )}
      {error && <Typography variant="caption" color="error" component="div" sx={{ mb: 0.5 }}>{error}</Typography>}
      {(allRow || done) && <Divider sx={{ my: 1 }} />}
      {(allRow || done) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {allRow && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }} data-testid="park-compare-all-row">
              <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="park-compare-all-label">{applyToAllLabel(total)}</Typography>
              <Button size="small" variant="outlined" disabled={busy} onClick={onKeepAll} data-testid="park-compare-keep-all">{PARK_KEEP_ALL_LABEL}</Button>
              <Button size="small" variant="contained" disabled={busy} onClick={onUseNewAll} data-testid="park-compare-new-all">{PARK_USE_NEW_ALL_LABEL}</Button>
            </Stack>
          )}
          {done && (
            <Button size="small" variant="contained" disabled={busy} onClick={onDone} sx={{ ml: 'auto !important' }} data-testid="park-compare-done">{PARK_DONE_LABEL}</Button>
          )}
        </Stack>
      )}
    </Paper>
  )
}
